import themeManifest from "../../../config/map-themes.json" with { type: "json" };

export const MAP_THEMES = themeManifest.themes;
export const MAP_THEME_GROUPS = themeManifest.groups;
export const MAP_THEME_CHOICES = MAP_THEMES;

const VALID_ACCESS = new Set(["free", "professional"]);
if (!Array.isArray(MAP_THEMES) || MAP_THEMES.some((theme) => !theme?.id || !VALID_ACCESS.has(theme.access))) {
  throw new Error("Invalid map-themes.json: every theme must declare access as free or professional");
}

const THEME_BY_ID = Object.fromEntries(MAP_THEMES.map((theme) => [theme.id, theme]));
const RESOLVED_THEME_CACHE = new Map();

export function getSelectableMapThemes({ canSelectProfessional = false, currentThemeId } = {}) {
  return MAP_THEMES.filter((theme) => (
    theme.access === "free" || canSelectProfessional || theme.id === currentThemeId
  ));
}
const HIERARCHY_DEFAULTS = {
  coreNodeWeight: 0.56,
  coreBorderWeight: 0.72,
  detailNodeWeight: 0.76,
  detailBorderWeight: 0.68,
  ...(themeManifest.hierarchyDefaults || {}),
};
const VISUAL_DEFAULTS = themeManifest.visualDefaults;
const VISUAL_PRESETS = themeManifest.visualPresets || {};
const KNOWLEDGE_GRAMMAR_BY_THEME = Object.freeze({
  // Editorial and print-oriented themes use restrained, monochrome structure.
  ink: "editorial", editorial: "editorial", swiss: "editorial", graphite: "editorial",
  // Technical themes emphasize alignment and crisp, categorical relationships.
  blueprint: "technical", systems: "technical", signal: "technical", underground: "technical",
  // Graphic themes use stronger outlines and relationship color as a visible key.
  bauhaus: "graphic", poster: "graphic", pulse: "graphic", bloom: "graphic",
  accessible: "graphic", "high-contrast": "graphic", citrus: "graphic", candy: "graphic",
  blocks: "graphic", arcade: "graphic", "neon-night": "graphic",
  // Natural themes lean on rounded concept cards and lighter canvas texture.
  tide: "organic", botanical: "organic", clay: "organic", dusk: "organic",
  mineral: "organic", solar: "organic", glacier: "organic", desert: "organic",
  wildflower: "organic", tropical: "organic", firefly: "organic",
  // Dense-map themes keep surfaces compact and move attention to the graph.
  minimal: "minimal", focus: "minimal", studio: "minimal", cloud: "minimal",
});
const KNOWLEDGE_GRAMMARS = Object.freeze({
  editorial: {
    nodeStyle: { focalRadius: 2, coreRadius: 2, supportingRadius: 2, detailRadius: 1, borderWidthScale: 1.05, widthScale: 1, heightScale: 1.04, paddingScale: 1.08, fontScale: 1.01, shadow: "none" },
    edgeStyle: { widthScale: 0.88, opacity: 0.92, markerScale: 0.92, colorMode: "monochrome" },
    relationshipLabelStyle: { fontSize: 10.5, fontWeight: 550, opacity: 0.96, haloPadding: 1.5, haloOpacity: 0.92, offset: 0 },
    typography: { focalWeight: 750, coreWeight: 650, supportingWeight: 500, detailWeight: 400 },
    gridStyle: { type: "none", opacity: 0, spacing: 28 },
  },
  technical: {
    nodeStyle: { focalRadius: 1, coreRadius: 1, supportingRadius: 1, detailRadius: 0, borderWidthScale: 1.12, widthScale: 0.93, heightScale: 0.92, paddingScale: 0.86, fontScale: 0.96, shadow: "none" },
    edgeStyle: { widthScale: 0.96, opacity: 0.96, markerScale: 1.02, colorMode: "categorical" },
    relationshipLabelStyle: { fontSize: 10.5, fontWeight: 600, opacity: 1, haloPadding: 2, haloOpacity: 1, offset: 0 },
    typography: { focalWeight: 800, coreWeight: 700, supportingWeight: 550, detailWeight: 450 },
    gridStyle: { type: "cross", opacity: 0.13, spacing: 28 },
  },
  graphic: {
    nodeStyle: { focalRadius: 4, coreRadius: 3, supportingRadius: 3, detailRadius: 2, borderWidthScale: 1.35, widthScale: 1, heightScale: 1, paddingScale: 1, fontScale: 1, focalScale: 1.08, coreScale: 1.04, detailScale: 0.98, shadow: "lifted" },
    edgeStyle: { widthScale: 1.16, opacity: 0.98, markerScale: 1.08, colorMode: "categorical" },
    relationshipLabelStyle: { fontSize: 11.5, fontWeight: 650, opacity: 1, haloPadding: 2.5, haloOpacity: 1, offset: 0 },
    typography: { focalWeight: 850, coreWeight: 750, supportingWeight: 600, detailWeight: 500 },
    gridStyle: { type: "none", opacity: 0, spacing: 24 },
  },
  organic: {
    nodeStyle: { focalRadius: 18, coreRadius: 14, supportingRadius: 10, detailRadius: 7, borderWidthScale: 0.95, widthScale: 1, heightScale: 1.04, paddingScale: 1.08, fontScale: 1.01, shadow: "soft" },
    edgeStyle: { widthScale: 0.9, opacity: 0.88, markerScale: 0.96, colorMode: "categorical" },
    relationshipLabelStyle: { fontSize: 11, fontWeight: 500, opacity: 0.94, haloPadding: 2.25, haloOpacity: 0.94, offset: 1 },
    typography: { focalWeight: 780, coreWeight: 680, supportingWeight: 500, detailWeight: 400 },
    gridStyle: { type: "dots", opacity: 0.11, spacing: 30 },
  },
  minimal: {
    nodeStyle: { focalRadius: 3, coreRadius: 2, supportingRadius: 2, detailRadius: 1, borderWidthScale: 0.88, widthScale: 0.94, heightScale: 0.94, paddingScale: 0.9, fontScale: 0.96, shadow: "none" },
    edgeStyle: { widthScale: 0.82, opacity: 0.84, markerScale: 0.9, colorMode: "categorical" },
    relationshipLabelStyle: { fontSize: 10.5, fontWeight: 500, opacity: 0.9, haloPadding: 1, haloOpacity: 0.82, offset: 0 },
    typography: { focalWeight: 750, coreWeight: 650, supportingWeight: 500, detailWeight: 400 },
    gridStyle: { type: "none", opacity: 0, spacing: 30 },
  },
});
const NODE_SHADOWS = Object.freeze({
  none: { focal: "none", core: "none", supporting: "none", detail: "none" },
  soft: {
    focal: "0 0 0 5px color-mix(in srgb, var(--map-focal-border) 20%, transparent), 0 12px 28px rgba(30,41,59,0.18)",
    core: "0 0 0 3px color-mix(in srgb, var(--map-core-border) 14%, transparent), 0 8px 18px rgba(30,41,59,0.14)",
    supporting: "3px 6px 6px rgba(15,23,42,0.12)",
    detail: "1px 3px 4px rgba(15,23,42,0.08)",
  },
  lifted: {
    focal: "0 0 0 5px color-mix(in srgb, var(--map-focal-border) 22%, transparent), 0 14px 26px rgba(30,41,59,0.22)",
    core: "0 0 0 3px color-mix(in srgb, var(--map-core-border) 18%, transparent), 0 9px 16px rgba(30,41,59,0.17)",
    supporting: "0 5px 10px rgba(15,23,42,0.16)",
    detail: "0 3px 6px rgba(15,23,42,0.12)",
  },
});

export function getMapNodeShadowCssVariables(shadow = "soft") {
  const values = NODE_SHADOWS[shadow] || NODE_SHADOWS.soft;
  return Object.fromEntries(Object.entries(values).map(([level, value]) => [`--map-node-shadow-${level}`, value]));
}
const GRID_TYPES = new Set(["dots", "lines", "cross", "none"]);
export const MAP_GRID_CHOICES = [
  { id: "theme", name: "Theme", description: "Use the grid designed for this theme" },
  { id: "dots", name: "Dots", description: "A quiet point grid" },
  { id: "lines", name: "Lines", description: "A continuous alignment grid" },
  { id: "cross", name: "Crosses", description: "Technical registration marks" },
  { id: "off", name: "Off", description: "A clean canvas without a grid" },
];

export function normalizeGridMode(gridMode, showGrid = true) {
  if (showGrid === false) return "off";
  return MAP_GRID_CHOICES.some((choice) => choice.id === gridMode) ? gridMode : "theme";
}

export function resolveGridStyle(theme, gridMode, showGrid = true) {
  const mode = normalizeGridMode(gridMode, showGrid);
  if (mode === "off") return { ...theme.gridStyle, type: "none", mode };
  if (mode === "theme") return { ...theme.gridStyle, mode };
  return { ...theme.gridStyle, type: mode, mode };
}

function finiteInRange(value, fallback, minimum, maximum) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : fallback;
}

function resolveVisualGrammar(theme) {
  const preset = VISUAL_PRESETS[theme.visualPreset] || {};
  const grammarId = theme.knowledgeGrammar || KNOWLEDGE_GRAMMAR_BY_THEME[theme.id] || "balanced";
  const grammar = KNOWLEDGE_GRAMMARS[grammarId] || {};
  const nodeStyle = { ...VISUAL_DEFAULTS.nodeStyle, ...(preset.nodeStyle || {}), ...(theme.nodeStyle || {}), ...(grammar.nodeStyle || {}) };
  const edgeStyle = { ...VISUAL_DEFAULTS.edgeStyle, ...(preset.edgeStyle || {}), ...(theme.edgeStyle || {}), ...(grammar.edgeStyle || {}) };
  const relationshipLabelStyle = { ...VISUAL_DEFAULTS.relationshipLabelStyle, ...(preset.relationshipLabelStyle || {}), ...(theme.relationshipLabelStyle || {}), ...(grammar.relationshipLabelStyle || {}) };
  const typography = { ...VISUAL_DEFAULTS.typography, ...(preset.typography || {}), ...(theme.typography || {}), ...(grammar.typography || {}) };
  const gridStyle = { ...VISUAL_DEFAULTS.gridStyle, ...(preset.gridStyle || {}), ...(theme.gridStyle || {}), ...(grammar.gridStyle || {}) };
  return {
    ...theme,
    knowledgeGrammar: grammarId,
    nodeStyle: {
      focalRadius: finiteInRange(nodeStyle.focalRadius, 10, 0, 28),
      coreRadius: finiteInRange(nodeStyle.coreRadius, 8, 0, 28),
      supportingRadius: finiteInRange(nodeStyle.supportingRadius, 6, 0, 28),
      detailRadius: finiteInRange(nodeStyle.detailRadius, 4, 0, 28),
      borderWidthScale: finiteInRange(nodeStyle.borderWidthScale, 1, 0.6, 1.6),
      shadow: ["none", "soft", "lifted"].includes(nodeStyle.shadow) ? nodeStyle.shadow : "soft",
      widthScale: finiteInRange(nodeStyle.widthScale, 1, 0.82, 1.08),
      heightScale: finiteInRange(nodeStyle.heightScale, 1, 0.82, 1.2),
      paddingScale: finiteInRange(nodeStyle.paddingScale, 1, 0.75, 1.25),
      fontScale: finiteInRange(nodeStyle.fontScale, 1, 0.85, 1.2),
      focalScale: finiteInRange(nodeStyle.focalScale, 1, 0.9, 1.2),
      coreScale: finiteInRange(nodeStyle.coreScale, 1, 0.9, 1.15),
      detailScale: finiteInRange(nodeStyle.detailScale, 1, 0.85, 1.05),
    },
    edgeStyle: {
      widthScale: finiteInRange(edgeStyle.widthScale, 1, 0.6, 1.6),
      opacity: finiteInRange(edgeStyle.opacity, 1, 0.6, 1),
      markerScale: finiteInRange(edgeStyle.markerScale, 1, 0.75, 1.3),
      colorMode: edgeStyle.colorMode === "monochrome" ? "monochrome" : "categorical",
    },
    relationshipLabelStyle: {
      fontSize: finiteInRange(relationshipLabelStyle.fontSize, 12, 10, 14),
      fontWeight: finiteInRange(relationshipLabelStyle.fontWeight, 400, 400, 700),
      opacity: finiteInRange(relationshipLabelStyle.opacity, 1, 0.8, 1),
      haloPadding: finiteInRange(relationshipLabelStyle.haloPadding, 2.5, 0, 5),
      haloOpacity: finiteInRange(relationshipLabelStyle.haloOpacity, 0, 0, 1),
      offset: finiteInRange(relationshipLabelStyle.offset, 0, -4, 4),
    },
    typography: {
      focalWeight: finiteInRange(typography.focalWeight, 750, 600, 900),
      coreWeight: finiteInRange(typography.coreWeight, 650, 500, 850),
      supportingWeight: finiteInRange(typography.supportingWeight, 525, 400, 750),
      detailWeight: finiteInRange(typography.detailWeight, 450, 400, 650),
    },
    gridStyle: {
      type: GRID_TYPES.has(gridStyle.type) ? gridStyle.type : "dots",
      opacity: finiteInRange(gridStyle.opacity, 1, 0, 1),
      spacing: finiteInRange(gridStyle.spacing, 16, 8, 40),
    },
  };
}

function hexChannels(color) {
  const normalized = String(color || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  return [0, 2, 4].map((offset) => parseInt(normalized.slice(offset, offset + 2), 16));
}

function mixHex(first, second, firstWeight) {
  const firstChannels = hexChannels(first);
  const secondChannels = hexChannels(second);
  if (!firstChannels || !secondChannels) return first;
  const weight = Math.max(0, Math.min(1, Number(firstWeight)));
  return `#${firstChannels.map((channel, index) => Math.round(
    channel * weight + secondChannels[index] * (1 - weight)
  ).toString(16).padStart(2, "0")).join("")}`;
}

function luminance(color) {
  const channels = hexChannels(color) || [255, 255, 255];
  const linear = channels.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

function compositeHex(foreground, background, opacity) {
  const foregroundChannels = hexChannels(foreground);
  const backgroundChannels = hexChannels(background);
  if (!foregroundChannels || !backgroundChannels) return background;
  const alpha = Math.max(0, Math.min(1, Number(opacity)));
  return `#${foregroundChannels.map((channel, index) => Math.round(
    channel * alpha + backgroundChannels[index] * (1 - alpha)
  ).toString(16).padStart(2, "0")).join("")}`;
}

function contrastRatio(first, second) {
  const values = [luminance(first), luminance(second)].sort((a, b) => b - a);
  return (values[0] + 0.05) / (values[1] + 0.05);
}

/** Adjust a theme accent only as much as needed to preserve contrast on its canvas. */
export function ensureColorContrast(color, background, minimumRatio = 3, opacity = 1) {
  const channels = hexChannels(color);
  const backgroundChannels = hexChannels(background);
  if (!channels || !backgroundChannels) return color;
  const canvas = background;
  const targetRatio = Number.isFinite(Number(minimumRatio)) ? Number(minimumRatio) : 3;
  const alpha = Math.max(0, Math.min(1, Number(opacity)));
  const visibleRatio = (candidate) => contrastRatio(compositeHex(candidate, canvas, alpha), canvas);
  if (visibleRatio(color) >= targetRatio) return color;

  const alternatives = ["#000000", "#ffffff"].sort((first, second) => visibleRatio(second) - visibleRatio(first));
  const target = hexChannels(alternatives[0]);
  let low = 0;
  let high = 1;
  for (let iteration = 0; iteration < 14; iteration += 1) {
    const amount = (low + high) / 2;
    const candidate = `#${channels.map((channel, index) => Math.round(
      channel + (target[index] - channel) * amount
    ).toString(16).padStart(2, "0")).join("")}`;
    if (visibleRatio(candidate) >= targetRatio) high = amount;
    else low = amount;
  }
  return `#${channels.map((channel, index) => Math.round(
    channel + (target[index] - channel) * high
  ).toString(16).padStart(2, "0")).join("")}`;
}

// Keep watermark contrast aligned with the theme's existing dark/light
// classification. Non-hex and transparent backgrounds are treated as light.
export function isDarkBackground(color) {
  return luminance(color) < 0.2;
}

function resolveHierarchyColors(theme) {
  // Details recede toward the canvas on dark themes. On light themes, the grid
  // supplies a restrained tint even when both the node and canvas are white.
  const detailTarget = luminance(theme.background) < luminance(theme.node)
    ? theme.background
    : theme.grid;
  return resolveVisualGrammar({
    ...theme,
    focalNode: theme.focalNode || theme.coreNode,
    focalBorder: theme.focalBorder || theme.coreBorder,
    coreLevelNode: theme.coreLevelNode || mixHex(
      theme.coreNode,
      theme.node,
      HIERARCHY_DEFAULTS.coreNodeWeight
    ),
    coreLevelBorder: theme.coreLevelBorder || mixHex(
      theme.coreBorder,
      theme.nodeBorder,
      HIERARCHY_DEFAULTS.coreBorderWeight
    ),
    detailNode: theme.detailNode || mixHex(
      theme.node,
      detailTarget,
      HIERARCHY_DEFAULTS.detailNodeWeight
    ),
    detailBorder: theme.detailBorder || mixHex(
      theme.nodeBorder,
      detailTarget,
      HIERARCHY_DEFAULTS.detailBorderWeight
    ),
  });
}

export function getCanvasOptions(themeId) {
  const theme = THEME_BY_ID[themeId] || THEME_BY_ID[themeManifest.defaultTheme];
  if (theme.canvas === "paper") return [{ id: "theme", name: "Paper" }];
  if (theme.canvas === "dark") return [{ id: "theme", name: "Dark" }];
  const themeName = isDarkBackground(theme.background) ? "Dark" : "Tinted";
  return [{ id: "theme", name: themeName }, { id: "paper", name: "Paper" }];
}

export function normalizeCanvasStyle(themeId, canvasStyle) {
  const options = getCanvasOptions(themeId);
  return options.some((option) => option.id === canvasStyle) ? canvasStyle : options[0].id;
}

export function getMapTheme(themeId, canvasStyle = "theme") {
  const base = THEME_BY_ID[themeId] || THEME_BY_ID[themeManifest.defaultTheme];
  const normalizedCanvas = normalizeCanvasStyle(base.id, canvasStyle);
  const key = `${base.id}:${normalizedCanvas}`;
  if (RESOLVED_THEME_CACHE.has(key)) return RESOLVED_THEME_CACHE.get(key);
  const theme = normalizedCanvas !== "paper"
    ? resolveHierarchyColors(base)
    : resolveHierarchyColors({
      ...base,
      background: "#ffffff",
      grid: "#d9dee3",
      node: "#ffffff",
      labelHalo: "#ffffff",
      ...(base.paperOverride || {}),
    });
  RESOLVED_THEME_CACHE.set(key, theme);
  return theme;
}

export function getThemeCssVariables(themeId, canvasStyle) {
  const theme = getMapTheme(themeId, canvasStyle);
  return {
    "--map-background": theme.background,
    "--map-grid": theme.grid,
    "--map-node": theme.node,
    "--map-node-border": theme.nodeBorder,
    "--map-text": theme.text,
    "--map-focal-node": theme.focalNode,
    "--map-focal-border": theme.focalBorder,
    "--map-core-node": theme.coreLevelNode,
    "--map-core-border": theme.coreLevelBorder,
    "--map-detail-node": theme.detailNode,
    "--map-detail-border": theme.detailBorder,
    "--map-label-halo": theme.labelHalo,
    "--map-node-radius-focal": `${theme.nodeStyle.focalRadius}px`,
    "--map-node-radius-core": `${theme.nodeStyle.coreRadius}px`,
    "--map-node-radius-supporting": `${theme.nodeStyle.supportingRadius}px`,
    "--map-node-radius-detail": `${theme.nodeStyle.detailRadius}px`,
    "--map-node-border-scale": theme.nodeStyle.borderWidthScale,
    "--map-node-weight-focal": theme.typography.focalWeight,
    "--map-node-weight-core": theme.typography.coreWeight,
    "--map-node-weight-supporting": theme.typography.supportingWeight,
    "--map-node-weight-detail": theme.typography.detailWeight,
    "--map-edge-label-size": `${theme.relationshipLabelStyle.fontSize}px`,
    "--map-edge-label-weight": theme.relationshipLabelStyle.fontWeight,
    "--map-edge-label-halo-width": `${theme.relationshipLabelStyle.haloPadding * 2}px`,
    "--map-edge-label-halo-opacity": theme.relationshipLabelStyle.haloOpacity,
    ...getMapNodeShadowCssVariables(theme.nodeStyle.shadow),
  };
}

function normalizeRelationshipLabel(label) {
  return String(label || "relationship").trim().toLowerCase() || "relationship";
}

function relationshipHash(label) {
  // FNV-1a gives short, similar relationship labels a substantially better
  // spread than the former weighted character sum while remaining fast and
  // deterministic in every renderer.
  let hash = 0x811c9dc5;
  for (const character of normalizeRelationshipLabel(label)) {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

export function relationshipColor(label, themeId, canvasStyle) {
  const palette = getMapTheme(themeId, canvasStyle).palette;
  return palette[relationshipHash(label) % palette.length];
}

export function relationshipColorAssignments(labels, themeId, canvasStyle) {
  const palette = getMapTheme(themeId, canvasStyle).palette;
  const normalizedLabels = [...new Set(labels.map(normalizeRelationshipLabel))].sort();
  const usage = Array.from({ length: palette.length }, () => 0);
  const assignments = new Map();

  for (const label of normalizedLabels) {
    const preferredIndex = relationshipHash(label) % palette.length;
    let selectedIndex = preferredIndex;

    // Preserve the hash as the preferred color, but resolve collisions toward
    // the least-used nearby palette entry. Distinct relationship types use the
    // full theme palette before colors repeat; identical labels still match.
    for (let offset = 1; offset < palette.length; offset += 1) {
      const candidateIndex = (preferredIndex + offset) % palette.length;
      if (usage[candidateIndex] < usage[selectedIndex]) selectedIndex = candidateIndex;
      if (usage[selectedIndex] === 0) break;
    }

    usage[selectedIndex] += 1;
    assignments.set(label, palette[selectedIndex]);
  }

  return assignments;
}
